import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mic, Lock, Loader2, CheckCircle, XCircle, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. The link may have expired.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#1F2A37] flex flex-col">
        {/* Hero Section */}
        <div className="flex flex-col items-center justify-center px-6 pt-10 pb-6">
          <div className="relative mb-5">
            <div className="absolute inset-0 w-20 h-20 bg-orange-500/20 rounded-full blur-xl animate-pulse"></div>
            <div className="relative w-20 h-20 flex items-center justify-center">
              <div className="absolute w-20 h-20 border-2 border-orange-500/30 rounded-full"></div>
              <div className="absolute w-16 h-16 border-2 border-orange-500/40 rounded-full"></div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Mic className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight mb-1">2Tap</h1>
          <p className="text-lg text-orange-400 font-medium">Password Reset</p>
        </div>

        {/* Content Card */}
        <div className="flex-1 bg-white rounded-t-3xl shadow-2xl px-6 pt-8 pb-8">
          <div className="w-full max-w-sm mx-auto text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Password Updated!</h2>
            <p className="text-slate-600 text-sm mb-6">
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <Button
              data-testid="go-to-login-btn"
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-[#1F2A37] hover:bg-slate-800 text-white font-semibold rounded-xl"
            >
              Go to Sign In
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error state (no token)
  if (error && !token) {
    return (
      <div className="min-h-screen bg-[#1F2A37] flex flex-col">
        <div className="flex flex-col items-center justify-center px-6 pt-10 pb-6">
          <div className="relative mb-5">
            <div className="absolute inset-0 w-20 h-20 bg-orange-500/20 rounded-full blur-xl animate-pulse"></div>
            <div className="relative w-20 h-20 flex items-center justify-center">
              <div className="absolute w-20 h-20 border-2 border-orange-500/30 rounded-full"></div>
              <div className="absolute w-16 h-16 border-2 border-orange-500/40 rounded-full"></div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Mic className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight mb-1">2Tap</h1>
          <p className="text-lg text-orange-400 font-medium">Password Reset</p>
        </div>

        <div className="flex-1 bg-white rounded-t-3xl shadow-2xl px-6 pt-8 pb-8">
          <div className="w-full max-w-sm mx-auto text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Invalid Link</h2>
            <p className="text-slate-600 text-sm mb-6">{error}</p>
            <Button
              data-testid="request-new-reset-btn"
              onClick={() => navigate('/forgot-password')}
              className="w-full h-12 bg-[#1F2A37] hover:bg-slate-800 text-white font-semibold rounded-xl"
            >
              Request New Reset Link
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-[#1F2A37] flex flex-col">
      {/* Hero Section */}
      <div className="flex flex-col items-center justify-center px-6 pt-10 pb-6">
        <div className="relative mb-5">
          <div className="absolute inset-0 w-20 h-20 bg-orange-500/20 rounded-full blur-xl animate-pulse"></div>
          <div className="relative w-20 h-20 flex items-center justify-center">
            <div className="absolute w-20 h-20 border-2 border-orange-500/30 rounded-full"></div>
            <div className="absolute w-16 h-16 border-2 border-orange-500/40 rounded-full"></div>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg shadow-orange-500/30">
              <Mic className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
          </div>
        </div>
        <h1 className="text-3xl font-bold text-white tracking-tight mb-1">2Tap</h1>
        <p className="text-lg text-orange-400 font-medium">Create New Password</p>
      </div>

      {/* Form Card */}
      <div className="flex-1 bg-white rounded-t-3xl shadow-2xl px-6 pt-6 pb-8">
        <div className="w-full max-w-sm mx-auto">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-800">Set New Password</h2>
            <p className="text-sm text-slate-500 mt-1">Enter your new password below</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium text-sm">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  data-testid="new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 pl-10 pr-10 bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700 font-medium text-sm">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  data-testid="confirm-password-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12 pl-10 pr-10 bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              data-testid="reset-password-btn"
              className="w-full h-12 bg-[#1F2A37] hover:bg-slate-800 text-white font-semibold rounded-xl"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Reset Password
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Remember your password?{' '}
              <Link 
                to="/login" 
                className="text-orange-600 hover:text-orange-700 font-semibold"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
